import {
  capitalizeFirstLetter,
  formatDecimal,
  formatInteger,
  pluralWord,
  round,
} from '@gozoki/tools'
import { DateTime } from 'luxon'
import { PILOTAGE_WEEKDAYS, PILOTAGE_ZONE } from '../-shared/pilotage-call-track'
import { formatCoefValue } from '../-shared/coef-slider-scale'

/**
 * Mise en forme des chiffres de la vue Réappro, à la française : « 2 191 », « 8,3 % », « ×0,80 ».
 * Une valeur absente ou non finie s'écrit « — ».
 */

const DASH = '—'

const finite = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value)

/** UVC entières : « 2 191 ». */
export const formatUnits = (value: number | null | undefined): string =>
  finite(value) ? formatInteger(value) : DASH

/** Pourcentage : « 8,3 % » (une décimale par défaut). */
export const formatPercent = (value: number | null | undefined, digits: 0 | 1 = 1): string =>
  finite(value) ? `${formatDecimal(value, digits)} %` : DASH

/** Coef de sécurité : « ×0,80 ». */
export const formatCoef = (value: number | null | undefined): string =>
  finite(value) ? `×${formatCoefValue(value)}` : DASH

/** Commande pour 100 UVC prévues : « 122 », « — » sans prévision. */
export const formatPer100 = (order: number, forecast: number): string =>
  forecast > 0 ? formatUnits((order / forecast) * 100) : DASH

const MINUS = '−'

/** Écart en points au dixième, le chiffre de `formatPoints` : symétrique autour de zéro. */
const roundPoints = (value: number): number => Math.sign(value) * round(Math.abs(value), 1)

/** Écart en points : « +0,3 pt », « −2,4 pts », « 0,0 pt ». */
export const formatPoints = (value: number | null | undefined): string => {
  if (!finite(value)) return DASH
  const rounded = Math.abs(roundPoints(value))
  let sign = ''
  if (rounded > 0) sign = value > 0 ? '+' : MINUS
  return `${sign}${formatDecimal(rounded, 1)} ${pluralWord(rounded, 'pt')}`
}

/** Multiplicateur en % : « +10 % », « 0 % », « −5 % ». */
export const formatMultiplier = (value: number | null | undefined): string => {
  if (!finite(value)) return DASH
  let sign = ''
  if (value !== 0) sign = value > 0 ? '+' : MINUS
  return `${sign}${formatDecimal(Math.abs(value), 1).replace(/,0$/, '')} %`
}

/** Jours : « 1 j », « 3 j ». */
export const formatDays = (count: number): string => `${count} j`

/** « mer. 28 » pour une date ISO (jour calendaire de Paris). */
export const formatShortDay = (iso: string): string => {
  const date = DateTime.fromISO(iso.slice(0, 10), { zone: PILOTAGE_ZONE })
  return `${PILOTAGE_WEEKDAYS[date.weekday - 1]} ${String(date.day).padStart(2, '0')}`
}

/** Jours couverts : « mer. 28 (1 j) », « sam. 31 → dim. 01 (2 j) ». */
export const formatCoveredDays = (dates: readonly string[]): string => {
  const first = dates[0]
  if (first === undefined) return DASH
  const last = dates.at(-1)!
  const span =
    dates.length > 1 ? `${formatShortDay(first)} → ${formatShortDay(last)}` : formatShortDay(first)
  return `${span} (${formatDays(dates.length)})`
}

/** Libellé d'une catégorie, en majuscules en base : « ENCAS SUCRES » → « Encas sucres ». */
export const formatCategoryLabel = (libelle: string): string =>
  capitalizeFirstLetter(libelle.toLocaleLowerCase('fr'))

/** Niveau d'alerte de surproduction du Sélecteur : « 1,5 ». */
export const formatSurprodK = (k: number): string => String(k).replace('.', ',')
